/*!
 * kr-pardot-consent.js — Kimes Ranch candidate, 2026-10-14 (rev 6).
 * Consent-gated Salesforce Account Engagement (Pardot) tracker, isolated in a
 * disposable same-origin iframe. NOT production verified.
 *
 * Why an iframe. pdt.js applies whatever its /analytics response says — opt-in
 * cookie, visitor-id cookies, follow-up hits to every cookie domain — with no
 * staleness check and no public way to cancel a request. Removing the iframe
 * removes the vendor's document. Per HTML "abort a document", that cancels its
 * fetches and discards their queued tasks, and a document without a browsing
 * context is cookie-averse. A response arriving after withdrawal therefore has
 * nowhere to run and nothing it can write.
 *
 * Not a security boundary. The frame carries allow-same-origin because pdt.js
 * needs document.cookie, so it shares the storefront's origin, cookie jar and
 * storage, and its script could reach window.parent. The iframe is only a
 * lifecycle and cancellation boundary for a vendor script that is trusted to
 * the same degree it was when it ran in the page. The cancellation itself is
 * browser behaviour that must be confirmed per browser, not assumed from here.
 *
 * Consent source. Shopify Customer Privacy API only. Tracking runs only when
 * analytics AND marketing are explicitly "yes", Shopify's own *Allowed() checks
 * agree, and Global Privacy Control is not set. No decision, API error, or API
 * absent all mean "not allowed".
 *
 * Fail closed. If the API disappears or errors while the vendor is live, the
 * tracker is stopped exactly as for a withdrawal. If the API is merely absent
 * while nothing is live (page start-up, before Shopify.loadFeatures resolves),
 * nothing is started and nothing is wiped, so a consenting visitor's identity
 * survives API start-up.
 *
 * Withdrawal order. (1) remove the tracker frame, (2) immediately set the
 * opt-in cookie to false and expire the visitor-id cookies on every domain
 * scope the old pd.js snippet could have used, (3) only then download pdt.js
 * into a fresh inert frame to send the vendor's revoke notices. Local state
 * never waits for the vendor download. The notices carry no visitor id.
 *
 * Revoke hosts (rev 5). pdt.js addresses its revoke notice to the cookie
 * domains it learned from an earlier /analytics JSON response plus its create
 * host. A fresh revoke frame has learned nothing, so on its own it would notify
 * go.kimesranch.com only, while the live accepted response (2026-10-14) lists
 * go.kimesranch.com and pi.pardot.com and both receive an identified sync hit
 * under consent. The revoke frame therefore calls the supported create() once
 * per host in the fixed REVOKE_HOSTS list and revokeConsent() after each: one
 * unidentified notice per known host. The list is a constant; no host learned
 * at runtime is ever used, and no host outside the vendor's own is contacted.
 * If one host's call throws, the other is still attempted once; the frame is
 * kept so a notice already in flight is not aborted. Only if every host throws
 * does the frame go and the local cleanup stand alone.
 *
 * Empty page-view response (rev 5, observed 2026-10-14; recovery reworked in
 * rev 6). The tracker host keeps its own opt-in cookie, host-only on
 * go.kimesranch.com, which this page can neither read nor clear. After a
 * withdrawal it is false. A same-page re-accept page view then carries
 * pi_opt_in=true in the query but false in the Cookie header; the server flips
 * its cookie to true, answers HTTP 200 with an empty body and issues no
 * identity, and pdt.js treats an empty body as a no-op. Only the next page
 * view is identified. Remedy, bounded: XMLHttpRequest.prototype.open is
 * wrapped in the disposable tracker frame's own realm, before pdt.js loads, so
 * the bridge can attach a loadend listener to the vendor's own request. Native
 * open/send run unchanged with the same arguments, the vendor's
 * onreadystatechange is untouched and runs first, and the storefront window's
 * XMLHttpRequest is never touched. The listener acts only when ALL of these
 * hold: the request was a GET to exactly https://<trackerHost>/analytics with
 * this account and campaign, pi_opt_in=true, no visitor_id / visitor_id_sign
 * and no conly / clear / revoke_consent / fetch_banner; the response settled
 * with status 200 and an empty body; the frame is still the live tracker frame
 * in phase 'active'; the single recovery of this activation has not been used;
 * no visitor cookie exists now; and consent is re-read as allowed at that
 * moment (if not, this is a withdrawal). Then ONE recovery page view is issued
 * from a fresh tracker object (next paragraph). No timers, no polling, no
 * retry on JSON (identity, denial, banner), on non-200, on aborts, on stale
 * frames or on identified requests, and never twice per activation.
 *
 * Fresh tracker object for the recovery (rev 6). pdt.js reads pi_em and
 * pi_content from document.URL once, when create() builds its tracker, into
 * private variables, and its response callback nulls both on every non-sync
 * HTTP 200 response, the empty transition reply included, before it looks at
 * the body. The supported set() command writes only the keys the vendor
 * exposes (url, title, referrer, piEmail, piProfileId, utm*), so the rev 5
 * recovery, a sendPageView on the consumed tracker, lost pi_em and pi_content
 * (reproduced offline against the captured pdt.js, 2026-10-14). The recovery
 * therefore repeats the supported start-up sequence on the SAME live tracker
 * frame: create() (re-reads the mirrored document.URL, recapturing every
 * query token), the set() fallback if the document could not be mirrored,
 * setOptIn(true), sendPageView. create() is synchronous and replaces the
 * vendor's tracker object; the consumed one holds no pending request (its
 * only request has just settled) and is never addressed again. No new frame,
 * no second pdt.js download, and no asynchronous gap between the consent
 * re-read and the vendor calls. The budget is one recovery per activation,
 * reset only when a consent change starts a new tracker frame; a recovery
 * whose own response is also empty ends the matter. Tokens the first page
 * view never carried (document mirror unavailable, or a landing URL without
 * them) are neither recovered nor invented.
 *
 * Boundary re-check. pdt.js arrives asynchronously. Consent is read again the
 * moment it is ready, before setOptIn/sendPageView, because the API may have
 * vanished, GPC may have been set, or consent may have changed in another tab
 * without any event reaching this page. Stale consent is a withdrawal.
 *
 * Failure. If the vendor throws while being initialised (after the frame was
 * created), the frame is discarded, the local opt-out above is applied at
 * once, and — only if a page view may already have been dispatched — one
 * unidentified revoke notice per known host is attempted. That attempt cannot
 * fail back into this path, so there is no retry loop; the phase stays
 * 'failed' until consent changes.
 *
 * Lifecycle (state.phase):
 *   idle      nothing loaded (declined / no choice / API unavailable)
 *   loading   tracker frame created, pdt.js loading
 *   active    pdt.js created, opt-in set, one page view sent (plus at most one
 *             recovery page view from a fresh tracker object after an empty
 *             response, see above)
 *   revoking  tracker frame discarded, local opt-out done, revoke frame loading
 *   revoked   revoke sent (or local cleanup only); no vendor code is live
 *   failed    vendor failed to load/initialise; local opt-out done; no retry
 *             until consent changes (a withdrawal moves it to 'revoked')
 */
(function (window, document) {
  'use strict';
  if (window.kimesPardotConsent) return;

  var CONFIG = {
    accountId: 1121213,
    campaignId: 142031,
    trackerHost: 'go.kimesranch.com',
    scriptUrl: 'https://go.kimesranch.com/pdt.js',
    /* Fixed list of hosts the vendor contacts for this account: the tracker
       host plus the cookie domain the live accepted response names. Never
       extended at runtime. */
    revokeHosts: ['go.kimesranch.com', 'pi.pardot.com']
  };
  var SUFFIX = String(CONFIG.accountId - 1000);               // vendor naming: account id minus 1000
  var OPT_IN_COOKIE = 'pi_opt_in' + SUFFIX;
  var VISITOR_COOKIES = ['visitor_id' + SUFFIX, 'visitor_id' + SUFFIX + '-hash'];
  var ALL_COOKIES = VISITOR_COOKIES.concat([OPT_IN_COOKIE]);
  var OPT_OUT_DAYS = 7;                                          // pdt.js default cookie duration
  var PAGEVIEW_PREFIX = 'https://' + CONFIG.trackerHost + '/analytics?';
  var PAGEVIEW_EXCLUDED = ['conly', 'clear', 'revoke_consent', 'fetch_banner', 'visitor_id', 'visitor_id_sign'];

  var state = {
    phase: 'idle',
    frame: null,
    pageviewSent: false,
    followUpSent: false,      // the single recovery page view of this activation has been issued
    observer: null,           // 'installed' | 'unavailable' for the current tracker frame
    urlMirrored: null,
    lastError: null
  };
  window.kimesPardotConsent = { state: state, sync: sync, version: '6-iframe' };

  /* ---------------------------------------------------------------- consent */

  function consentDecision() {
    try {
      if (window.navigator && window.navigator.globalPrivacyControl === true) return 'denied';
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return 'unknown';
      var consent = privacy.currentVisitorConsent();
      if (!consent || consent.analytics !== 'yes' || consent.marketing !== 'yes') return 'denied';
      if (privacy.analyticsProcessingAllowed() !== true) return 'denied';
      if (privacy.marketingAllowed() !== true) return 'denied';
      if (privacy.saleOfDataAllowed() !== true) return 'denied';
      return 'allowed';
    } catch (error) {
      state.lastError = error;
      return 'denied';
    }
  }

  function vendorLive() {
    return state.phase === 'loading' || state.phase === 'active';
  }

  function sync() {
    var decision = consentDecision();
    if (decision === 'allowed') {
      if (vendorLive() || state.phase === 'failed') return;
      discardFrame();                             // an unfinished revoke frame is dropped: the visitor re-consented
      start();
    } else if (decision === 'denied') {
      if (vendorLive() || state.phase === 'failed') stop();
      else if (hasVendorCookie()) expireCookies(ALL_COOKIES);   // leftovers from the old pd.js snippet; no session here
    } else if (vendorLive()) {
      stop();                                     // 'unknown' with the vendor live: consent basis gone, fail closed
    }
    /* 'unknown' with nothing live (idle / revoking / revoked / failed): no
       tracking and no cleanup, so identities are not wiped during API start-up. */
  }

  /* --------------------------------------------------------------- tracking */

  function start() {
    state.phase = 'loading';
    state.pageviewSent = false;
    state.followUpSent = false;
    state.observer = null;
    var frame;
    try { frame = createFrame('tracker'); } catch (error) { fail(error); return; }
    state.frame = frame;
    try {                                         // before pdt.js is requested; never blocks tracking
      state.observer = observePageViews(frame) ? 'installed' : 'unavailable';
    } catch (error) { state.lastError = error; state.observer = 'unavailable'; }
    loadVendor(frame, function () {
      if (consentDecision() !== 'allowed') { stop(); return; }   // consent lost while pdt.js downloaded, no event delivered
      activateTracker(frame);
      state.phase = 'active';
    }, fail);
  }

  /* Supported start-up sequence on the live tracker frame. Used once when
     pdt.js is ready and at most once more for the recovery after an empty
     page-view response (see header). The caller must have re-read consent as
     allowed immediately before; nothing asynchronous happens in here. */
  function activateTracker(frame) {
    createTracker(frame, CONFIG.trackerHost);     // fresh tracker object: query attribution read from the mirrored URL now
    if (!state.urlMirrored) {                     // supported fallback: page-level fields only
      command(frame, 'set', 'url', document.URL);
      command(frame, 'set', 'title', document.title);
      if (document.referrer) command(frame, 'set', 'referrer', document.referrer);
    }
    command(frame, 'setOptIn', true);
    state.pageviewSent = true;                    // set first: if sendPageView throws after dispatch, a revoke is still owed
    command(frame, 'sendPageView');
  }

  function stop() {
    if (state.frame && state.frame.kind === 'revoke') {   // notices owed by a failure are already on their way
      state.phase = 'revoking';
      return;
    }
    var needsVendorRevoke = state.pageviewSent || hasVendorCookie();
    discardFrame();                               // 1. aborts any in-flight vendor request
    state.pageviewSent = false;
    optOutLocally();                              // 2. immediately, before any vendor download
    if (!needsVendorRevoke) { state.phase = 'revoked'; return; }
    state.phase = 'revoking';                     // 3. vendor notices from a fresh inert frame
    sendRevokeNotice();
  }

  function fail(error) {
    state.lastError = error;
    var noticeOwed = state.pageviewSent;          // sendPageView may have dispatched before throwing
    discardFrame();                               // cancels anything the vendor had in flight
    state.pageviewSent = false;
    optOutLocally();                              // never leave the vendor's opt-in=true behind a failure
    state.phase = 'failed';
    if (noticeOwed) sendRevokeNotice();           // one attempt; its failure ends in localRevoke, never back here
  }

  /* One create() + revokeConsent() per known host: the vendor addresses the
     notice to its create host (plus cookie domains it has not learned here),
     so each host needs its own tracker object. Ids are already gone locally,
     so every notice is unidentified. A host whose call throws is recorded and
     skipped; the frame is only discarded if no host could be notified. */
  function sendRevokeNotice() {
    var frame;
    try { frame = createFrame('revoke'); } catch (error) { localRevoke(error); return; }
    state.frame = frame;
    loadVendor(frame, function () {
      var notified = 0, firstError = null;
      for (var i = 0; i < CONFIG.revokeHosts.length; i++) {
        try {
          createTracker(frame, CONFIG.revokeHosts[i]);
          command(frame, 'revokeConsent');        // vendor: opt-in=false again, one notice to this host
          notified++;
        } catch (error) { if (!firstError) firstError = error; }
      }
      if (!notified) throw firstError;            // lands in localRevoke via loadVendor's catch
      if (firstError) state.lastError = firstError;
      settleRevoke();
    }, localRevoke);
  }

  function localRevoke(error) {
    if (error) state.lastError = error;
    discardFrame();
    optOutLocally();
    settleRevoke();
  }

  function settleRevoke() {
    if (state.phase !== 'failed') state.phase = 'revoked';   // 'failed' stays terminal so nothing retries
  }

  /* ------------------------------------- empty page-view response follow-up */

  /* Wraps open() on the tracker frame's own XMLHttpRequest.prototype so the
     vendor's first page view can be watched. The wrapper only inspects the
     arguments, registers a loadend listener on strictly matching requests and
     then calls the native open() with the original this/arguments. Returns
     false (and installs nothing) when the frame offers no patchable XHR. */
  function observePageViews(frame) {
    var Xhr = frame.win.XMLHttpRequest;
    var proto = Xhr && Xhr.prototype;
    if (!proto || typeof proto.open !== 'function') return false;
    var nativeOpen = proto.open;
    proto.open = function (method, url) {
      try {
        if (!this._krPardotObserved && typeof this.addEventListener === 'function' &&
            isOptInPageView(method, url)) {
          this._krPardotObserved = true;
          var xhr = this;
          this.addEventListener('loadend', function () { onPageViewSettled(frame, xhr); });
        }
      } catch (error) { state.lastError = error; }
      return nativeOpen.apply(this, arguments);
    };
    return true;
  }

  /* Strict match: GET to exactly https://<trackerHost>/analytics, this account
     and campaign, pi_opt_in=true, no visitor id, none of the cookie-sync,
     clear, revoke or banner flags, no duplicate or undecodable keys. */
  function isOptInPageView(method, url) {
    if (String(method).toUpperCase() !== 'GET') return false;
    url = String(url);
    if (url.indexOf(PAGEVIEW_PREFIX) !== 0 || url.indexOf('#') !== -1) return false;
    var params = {};
    var pairs = url.slice(PAGEVIEW_PREFIX.length).split('&');
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      var key, value;
      try {
        key = decodeURIComponent(eq === -1 ? pairs[i] : pairs[i].slice(0, eq));
        value = decodeURIComponent(eq === -1 ? '' : pairs[i].slice(eq + 1));
      } catch (error) { return false; }
      if (Object.prototype.hasOwnProperty.call(params, key)) return false;
      params[key] = value;
    }
    for (var j = 0; j < PAGEVIEW_EXCLUDED.length; j++) {
      if (Object.prototype.hasOwnProperty.call(params, PAGEVIEW_EXCLUDED[j])) return false;
    }
    return params.account_id === String(CONFIG.accountId) &&
      params.campaign_id === String(CONFIG.campaignId) &&
      params.pi_opt_in === 'true';
  }

  /* Runs after the vendor's own readystatechange handler. Everything is
     re-checked here, at the moment of the decision, not at request time. */
  function onPageViewSettled(frame, xhr) {
    try {
      if (frame !== state.frame || frame.kind !== 'tracker' || state.phase !== 'active') return;   // stale frame
      if (state.followUpSent) return;                                    // one recovery per activation, ever
      if (xhr.status !== 200) return;                                    // error / abort / non-200: no retry
      if (typeof xhr.response !== 'string' || xhr.response.length !== 0) return;   // JSON was handled by the vendor
      if (hasVisitorIdentity()) return;                                  // identity exists: nothing to recover
      if (consentDecision() !== 'allowed') { stop(); return; }           // consent gone meanwhile: withdrawal
      state.followUpSent = true;                                         // set first so a throw cannot re-arm
      activateTracker(frame);                                            // fresh tracker object, not a resend on the consumed one
    } catch (error) { fail(error); }
  }

  /* ------------------------------------------------------------------ frame */

  function createFrame(kind) {
    var el = document.createElement('iframe');
    el.setAttribute('sandbox', 'allow-scripts allow-same-origin'); // same-origin is required: pdt.js needs document.cookie
    el.setAttribute('title', 'Pardot tracking (hidden)');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('tabindex', '-1');
    el.setAttribute('data-kr-pardot', kind);
    el.hidden = true;
    el.style.display = 'none';
    (document.body || document.documentElement).appendChild(el);
    var win = el.contentWindow;
    var doc = el.contentDocument;
    if (!win || !doc) {
      if (el.parentNode) el.parentNode.removeChild(el);
      throw new Error('iframe document unavailable');
    }
    return { el: el, win: win, doc: doc, kind: kind };
  }

  function discardFrame() {
    var frame = state.frame;
    state.frame = null;
    if (frame && frame.el && frame.el.parentNode) frame.el.parentNode.removeChild(frame.el);
  }

  function loadVendor(frame, onReady, onFail) {
    mirrorDocument(frame.doc);
    var script = frame.doc.createElement('script');
    script.async = true;
    script.src = CONFIG.scriptUrl;
    script.onload = function () {
      if (frame !== state.frame) return;          // frame discarded meanwhile; ignore
      try {
        if (typeof frame.win.pdt !== 'function') throw new Error('pdt api missing');
        onReady();
      } catch (error) { onFail(error); }          // anything thrown by the vendor or by onReady lands here
    };
    script.onerror = function () {
      if (frame === state.frame) onFail(new Error('pdt.js failed to load'));
    };
    (frame.doc.head || frame.doc.documentElement).appendChild(script);
  }

  function createTracker(frame, host) {
    command(frame, 'create', CONFIG.accountId, CONFIG.campaignId, host);
  }

  /* pdt.js reads document.URL / title / referrer at create time (URL query
     attribution such as gclid and pi_* comes only from document.URL). Shadow
     those three accessors on the throwaway frame document so the vendor sees
     the storefront page. Verified by read-back; on any mismatch the supported
     set() fallback in start() is used and state.urlMirrored reports false. */
  function mirrorDocument(doc) {
    var values = { URL: document.URL, title: document.title, referrer: document.referrer };
    var ok = true;
    for (var key in values) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) continue;
      try {
        Object.defineProperty(doc, key, { configurable: true, get: constant(values[key]) });
      } catch (error) { ok = false; }
      if (doc[key] !== values[key]) ok = false;
    }
    state.urlMirrored = ok;
  }

  function constant(value) { return function () { return value; }; }

  function command(frame) {
    var args = Array.prototype.slice.call(arguments, 1);
    var result = frame.win.pdt.apply(null, args);
    if (typeof result === 'string' && result.indexOf('ERR_') === 0) {
      throw new Error('pdt ' + args[0] + ': ' + result);
    }
    return result;
  }

  /* ---------------------------------------------------------------- cookies */

  function readCookie(name) {
    var all;
    try { all = document.cookie || ''; } catch (error) { return null; }
    var parts = all.split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].replace(/^\s+/, '');
      if (part.indexOf(name + '=') === 0) return part.slice(name.length + 1);
    }
    return null;
  }

  function hasVisitorIdentity() {
    return readCookie(VISITOR_COOKIES[0]) !== null || readCookie(VISITOR_COOKIES[1]) !== null;
  }

  function hasVendorCookie() {
    return hasVisitorIdentity() || readCookie(OPT_IN_COOKIE) === 'true';
  }

  /* Every scope a Pardot cookie could live in on this storefront: host-only,
     the exact host as a domain cookie, and the parent domain when on www. */
  function cookieDomains() {
    var host = (window.location && window.location.hostname) || '';
    var domains = [null];
    if (host) domains.push(host);
    if (host.indexOf('www.') === 0) domains.push(host.slice(4));
    return domains;
  }

  function expireCookies(names) {
    var domains = cookieDomains();
    for (var i = 0; i < names.length; i++) {
      for (var j = 0; j < domains.length; j++) {
        try {
          document.cookie = names[i] + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' +
            (domains[j] ? '; domain=' + domains[j] : '');
        } catch (error) { /* cookie-averse or blocked document: nothing to clean */ }
      }
    }
  }

  /* Withdrawal state the vendor itself would write (opt-in=false, host-only,
     7 days), after clearing every other scope so no stale "true" can shadow it. */
  function optOutLocally() {
    expireCookies(ALL_COOKIES);
    var expires = new Date();
    expires.setDate(expires.getDate() + OPT_OUT_DAYS);
    try {
      document.cookie = OPT_IN_COOKIE + '=false; expires=' + expires.toUTCString() + '; path=/';
    } catch (error) { /* nothing to write into */ }
  }

  /* ------------------------------------------------------------------ wiring */

  document.addEventListener('visitorConsentCollected', sync);
  window.addEventListener('pageshow', sync);
  window.addEventListener('focus', sync);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') sync();
  });
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) sync();
      });
    } catch (error) { state.lastError = error; }
  }
  sync();
})(window, document);
